import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  useLazyGetUserQuery,
  useLogoutMutation,
  useLoginUserMutation,
  usePatchUserProfileMutation,
} from 'lib.store/src/main/-user/user.api';
import { showApiError } from 'lib.shared/src/handleApiError';
import { initVisibilityAuthCheck } from 'lib.store/src/main/baseQueryWithReauth';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { store } from 'lib.store/src';
import { UserType } from 'lib.store/src/models-to-replace/auth';
import { userActions } from 'lib.store/src/main/-user/user.slice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'lib.hooks/src/redux';
import { useUserSettingsSync } from 'lib.hooks/src/useUserSettingsSync';
import { ErrorDataResponse, deleteCookie } from 'lib.shared/src/utils';
import {
  AUTH_PROVIDER,
  LAST_AUTH_METHOD,
  LastAuthMethod,
  updateLastAuthInfo,
} from 'lib.shared/src/auth/authStorage';
import * as Sentry from '@sentry/react';
import { useGetBillingInfoQuery } from 'lib.store/src/main/billing/billing.api';
import getProSubscription, { ProSubscriptionData } from 'lib.shared/src/getProSubscription';
import { message, notification } from 'antd';
import sendAnalytics from 'lib.hooks/src/sendAnalytics/sendAnalytics';
import { GTMEventName } from 'lib.hooks/src/sendAnalytics/constants';
import { TextBodyRegular } from 'lib.typography';
import { SubscriptionPlan } from 'lib.store/src/main/subscription/models';
import { usePostUpdateSubscriptionByIdMutation } from 'lib.store/src/main/subscription/subscription.api';
import { usePatchUserOragnizationTypeMutation } from 'lib.store/src/main/organization/organization.api';

/** Options of a user refresh. */
export interface ReFetchUserOptions {
  /**
   * Refresh without raising the global loading flag. Routes renders a loading
   * view while it is up, which unmounts the whole tree — a background refresh
   * (reading a fresh balance after the billing drawer closes, say) would then
   * throw the page that asked for it back to its initial state.
   */
  silent?: boolean;
}

interface AuthContextType {
  user?: UserType;
  reFetchUser: (options?: ReFetchUserOptions) => Promise<void>;
  userLoadingError?: FetchBaseQueryError | SerializedError;
  isUserLoading: boolean;
  isAuthenticated: boolean;
  logout: (path?: string) => void;
  login: (values: { email: string; password: string }) => Promise<boolean>;
  emailConfirmedData: {
    isConfirmed: boolean;
    error: string;
  };
  setEmailConfirmedData: ({ isConfirmed, error }: { isConfirmed: boolean; error: string }) => void;
  isHubspotChatOpen: boolean;
  setIsHubspotChatOpen: (isOpen: boolean) => void;
  proSubscriptionData: ProSubscriptionData;
  onUpdateSubscription: (url?: string, onError?: () => void) => void;
}
const isOutreachEnabled = import.meta.env.VITE_IS_OUTREACH_ENABLED as string;
const isBeta = isOutreachEnabled === 'true';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<PropsWithChildren> = (props) => {
  const [isHubspotChatOpen, setIsHubspotChatOpen] = useState(false);

  const { user, isUserLoading, userError } = useAppSelector((state) => state.userAuth);
  const dispatch = useDispatch();

  const [updateSubscriptionById] = usePostUpdateSubscriptionByIdMutation();
  const [onGetUser] = useLazyGetUserQuery();
  const [patchOrganizationType] = usePatchUserOragnizationTypeMutation();
  const [patchUser] = usePatchUserProfileMutation();

  const { data: billingInfo } = useGetBillingInfoQuery();

  // TODO: skip step if a free plan is selected
  const proSubscriptionData = useMemo(
    () => getProSubscription(user, billingInfo?.data),
    [user, billingInfo?.data],
  );
  useEffect(() => {
    localStorage.setItem(
      'email_verified_at',
      user?.email_verified_at ? user?.email_verified_at : '',
    );
  }, [user?.email_verified_at]);

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    const oauthMethods = {
      [AUTH_PROVIDER.GOOGLE]: LAST_AUTH_METHOD.GOOGLE_OAUTH,
      [AUTH_PROVIDER.LINKEDIN]: LAST_AUTH_METHOD.LINKEDIN_OAUTH,
    } as Record<string, LastAuthMethod>;

    const method =
      (user?.provider ? oauthMethods[user.provider] : undefined) ?? LAST_AUTH_METHOD.EMAIL_PASSWORD;

    updateLastAuthInfo({ method, email: user.email });
  }, [user?.email, user?.provider]);

  const handleGetUser = async (options?: ReFetchUserOptions): Promise<void> => {
    const isSilent = options?.silent === true;

    if (!isSilent) {
      dispatch(userActions.setIsUserLoading(true));
    }
    try {
      const response = await onGetUser();
      if (response?.data) {
        dispatch(userActions.setUserState(response?.data?.data));
        dispatch(userActions.setUserError(''));
      }
      if ((response as ErrorDataResponse)?.error?.data?.message) {
        dispatch(
          userActions.setUserError((response as ErrorDataResponse).error.data.message || ''),
        );
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(userActions.setUserState(null));
      } else {
        dispatch(userActions.setUserError(error?.message));
      }
    } finally {
      if (!isSilent) {
        dispatch(userActions.setIsUserLoading(false));
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-void
    void handleGetUser();
  }, []);

  useEffect(() => {
    if (!user?.id) {
      Sentry.setUser(null);
    } else {
      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
    }
    return () => {
      Sentry.setUser(null);
    };
  }, [user]);

  useEffect(() => {
    return initVisibilityAuthCheck(store.dispatch, store.getState);
  }, []);

  const [loginUser] = useLoginUserMutation();
  const [logoutUser] = useLogoutMutation();
  const [emailConfirmedData, setEmailConfirmedData] = useState({
    isConfirmed: false,
    error: '',
  });

  const { children } = props;

  const logout = async (path?: string) => {
    try {
      await logoutUser().then(() => {
        const nextPath = typeof path === 'string' ? path : '';
        window.location.replace(nextPath || '/login');
        deleteCookie('event_hash', { rootDomain: true });
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  const login = async (values: { email: string; password: string }) => {
    try {
      const response = await loginUser(values);
      if ('data' in response) {
        dispatch(userActions.setUserState(response.data?.data));
        dispatch(userActions.setUserError(''));
        await handleGetUser();
        updateLastAuthInfo({ method: LAST_AUTH_METHOD.EMAIL_PASSWORD, email: values.email });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error:', err);
      return false;
    }
  };

  const onUpdateSubscription = useCallback(
    async (url?: string, onError?: () => void) => {
      let newUserData;
      if (user?.organization?.onboarding !== 'ready' && user?.organization?.type !== 'partner') {
        try {
          newUserData = await patchOrganizationType({ organization_type: 'partner' });
        } catch (e) {
          showApiError(e, 'Failed to update organization type', true);
          return;
        }
      }
      if (
        user?.organization?.onboarding !== 'ready' &&
        user?.organization?.type === 'partner' &&
        isBeta
      ) {
        try {
          newUserData = await patchUser({ onboarding_status: 'ready' });
        } catch (e) {
          showApiError(e, 'Failed to update user profile', true);
          return;
        }
      }
      try {
        const selectedPlanData = sessionStorage.getItem(`selected-plan-data`)
          ? (JSON.parse(sessionStorage.getItem(`selected-plan-data`) || '') as SubscriptionPlan)
          : null;
        const selectedAnnualData = sessionStorage.getItem(`selected-is-annual-data`);
        const subscriptionUpdatePayload = selectedPlanData
          ? {
              id: selectedPlanData.plan.id,
              data: {
                annual: selectedAnnualData === '1',
                options: {
                  credits: selectedPlanData.money.credits,
                  price: selectedPlanData.money.price,
                },
              },
            }
          : {};
        const response = await updateSubscriptionById(subscriptionUpdatePayload).unwrap();
        if ('error' in response) {
          if (newUserData && 'data' in newUserData) {
            sendAnalytics(GTMEventName.partnerOnboardingThirdStep);
          }
          if (onError) {
            onError();
          }
          return void message.open({
            type: 'error',
            content:
              (response as { error: { data: { message: string } } })?.error?.data?.message ||
              'Subscription error',
          });
        }
        if ('data' in response) {
          sessionStorage.removeItem(`selected-plan-data`);
          sessionStorage.removeItem(`selected-is-annual-data`);
          if (newUserData && 'data' in newUserData) {
            const newUser = {
              ...newUserData?.data?.data,
              organization: {
                ...(newUserData?.data?.data?.organization || {}),
                credits: response?.data?.options?.credits || 0,
              },
            };
            dispatch(userActions.setUserState(newUser));
            dispatch(userActions.setUserError(''));
            sendAnalytics(GTMEventName.partnerOnboardingThirdStep);
          }
          localStorage.removeItem('event_hash');
          notification.open({
            message: (
              <span className="moderation-table__notification__message inline-flex">
                <TextBodyRegular className="text-neutral-grey-10">
                  “{selectedPlanData?.plan?.translate}” plan successfully activated
                </TextBodyRegular>
              </span>
            ),
            className: 'moderation-table__notification',
            placement: 'bottomLeft',
            closeIcon: false,
            type: 'success',
          });
          if (url) {
            location.replace(url);
          }
        }
      } catch (error) {
        if (newUserData && 'data' in newUserData) {
          dispatch(userActions.setUserState(newUserData?.data?.data));
          dispatch(userActions.setUserError(''));
          sendAnalytics(GTMEventName.partnerOnboardingThirdStep);
        }
        if (onError) {
          onError();
        }
        return void message.open({
          type: 'error',
          content: (error as { data: { message: string } })?.data?.message || 'Subscription error',
        });
      }
    },
    [user, dispatch, patchOrganizationType, updateSubscriptionById],
  );

  const isAuthenticated = !!user;

  // Sync user settings from backend to UserPreferences (localStorage) on startup
  useUserSettingsSync(isAuthenticated);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isUserLoading,
        userLoadingError: userError,
        login,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        logout,
        reFetchUser: handleGetUser,
        emailConfirmedData,
        setEmailConfirmedData,
        isHubspotChatOpen,
        setIsHubspotChatOpen,
        proSubscriptionData,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onUpdateSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
export { AuthProvider, useAuth };
